import { AuthenticatedRoute, AuthViewProps, GoogleCallback, UnauthenticatedRoute } from "@homebound/auth-components";
import { Navigate, Route, Routes as RouterRoutes } from "react-router-dom";
import { DevelopmentScheduleCostMappingPage } from "src/routes/developments/cost-mapping/DevelopmentScheduleCostMappingPage";
import { PersonalDashboardPage } from "src/routes/personal-dashboard/PersonalDashboard";
import { ProjectLayout } from "src/routes/projects/ProjectLayout";
import { SignIn } from "./routes/auth/SignIn";
import { AddBidPackagePage } from "./routes/bids/add-bid-package/AddBidPackagePage";
import { BidPackageDetailPage } from "./routes/bids/BidPackageDetailPage";
import { BidPackagesPage } from "./routes/bids/BidPackagesPage";
import { EnterPlanBasedBidPage } from "./routes/bids/plan-based-bid-package/EnterPlanBasedBidPage";
import { PlanBasedBidPackagePage } from "./routes/bids/plan-based-bid-package/PlanBasedBidPackagePage";
import { DevelopmentCommitmentPage } from "./routes/development-commitments/DevelopmentCommitmentPage";
import { DevelopmentCommitmentsPage } from "./routes/development-commitments/DevelopmentCommitmentsPage";
import { DevelopmentLayout } from "./routes/developments/DevelopmentLayout";
import { ReleaseToTradesPage } from "./routes/developments/lot-summary/lot-release/ReleaseToTradesPage";
import { EditOptionsPage } from "./routes/developments/plan-and-options/options/EditOptionsPage";
import { ReassignPurchaseOrdersPage } from "./routes/developments/reassign-purchase-orders/ReassignPurchaseOrdersPage";
import { ReviewAndPublishPage } from "./routes/developments/templates/review-and-publish/ReviewAndPublishPage";
import { ExpensesAdmin } from "./routes/expenses/ExpensesAdmin";
import { InboxPage } from "./routes/inbox/InboxPage";
import { TradesLayout } from "./routes/libraries/global-options/TradesLayout";
import { LibrariesLayout } from "./routes/libraries/LibrariesLayout";
import { NotFound } from "./routes/NotFound";
import { UserProfile } from "./routes/profile/UserProfile";
import {
  bidPackageDetailsPath,
  bidPackagesEditPath,
  bidPackagesPath,
  costMappingPath,
  createBidPackagePath,
  createPlanBasedBidPackagePath,
  developmentCommitmentsPath,
  developmentCommitmentsPaths,
  developmentPaths,
  enterPlanBasedBidPath,
  expensesPath,
  inboxPaths,
  librariesPath,
  personalDashboardPaths,
  planBasedBidPackagesEditPath,
  projectPaths,
  reassignPurchaseOrdersPath,
  searchPath,
  tradesPath,
} from "./routes/routesDef";
import { SearchPage } from "./routes/search/Search";
export type ProjectRouteProps = { projectId: string };

/**
 * LEGACY ROUTING — flat route tree for URLs handled by `src/App.tsx`.
 *
 * Migrated domains register in `src/routing/Router.tsx` instead; do not duplicate those paths here.
 * See `src/routing/README.md`.
 */
export function Routes({ authProps }: { authProps: AuthViewProps }) {
  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate to={personalDashboardPaths.base} replace />} />

      <Route element={<UnauthenticatedRoute authProps={authProps} />}>
        <Route path="/signIn" element={<SignIn {...authProps} />} />
        <Route path="/auth/gcallback" element={<GoogleCallback />} />
      </Route>

      <Route element={<AuthenticatedRoute authProps={authProps} />}>
        <Route path="/profile" element={<UserProfile {...authProps} />} />
        <Route path={`${projectPaths.releaseToTrades}/*`} element={<ReleaseToTradesPage />} />
        <Route path={`${reassignPurchaseOrdersPath}/*`} element={<ReassignPurchaseOrdersPage />} />
        <Route path={developmentPaths.reviewTemplates} element={<ReviewAndPublishPage />} />
        <Route path={developmentPaths.editOptions} element={<EditOptionsPage />} />
        <Route path={`${developmentPaths.development}/*`} element={<DevelopmentLayout />} />
        <Route path={costMappingPath} element={<DevelopmentScheduleCostMappingPage />} />
        <Route path={`${projectPaths.project}/*`} element={<ProjectLayout />} />
        <Route path={`${inboxPaths.base}/*`} element={<InboxPage />} />
        <Route path={personalDashboardPaths.base} element={<PersonalDashboardPage />} />
        <Route path={`${personalDashboardPaths.base}/:section/*`} element={<PersonalDashboardPage />} />
        {/* Support old `/my-blueprint` redirects for historical purposes */}
        <Route path="/my-blueprint" element={<Navigate to={personalDashboardPaths.actions} replace />} />
        <Route path="/my-blueprint/to-dos" element={<Navigate to={personalDashboardPaths.toDos} replace />} />
        <Route path="/my-blueprint/activity-feed" element={<Navigate to={personalDashboardPaths.activity} replace />} />
        {/* Finances (`/bills`, `/invoices`, `/purchase-orders`, `/invoice-terms`, `/lien-waivers`) is served by the data router (src/routing/domains/finances.ts). */}
        {/* `/admin/*` (including `/admin/tradeCategories`) is served by the data router (src/routing/domains/admin.tsx). */}
        {/* `/trade-partners/*` is served by the data router (src/routing/domains/tradePartners.ts). */}
        <Route path={createPlanBasedBidPackagePath} element={<PlanBasedBidPackagePage />} />
        <Route path={planBasedBidPackagesEditPath} element={<PlanBasedBidPackagePage />} />
        <Route path={enterPlanBasedBidPath} element={<EnterPlanBasedBidPage />} />
        <Route path={createBidPackagePath} element={<AddBidPackagePage />} />
        <Route path={bidPackagesEditPath} element={<AddBidPackagePage />} />
        <Route path={bidPackagesPath} element={<BidPackagesPage />} />
        <Route path={bidPackageDetailsPath} element={<BidPackageDetailPage />} />
        <Route path={`${tradesPath}/*`} element={<TradesLayout />} />
        <Route path={`${librariesPath}/*`} element={<LibrariesLayout />} />
        <Route path={developmentCommitmentsPath} element={<DevelopmentCommitmentsPage />} />
        <Route path={developmentCommitmentsPaths.developmentCommitment} element={<DevelopmentCommitmentPage />} />
        {/* `/change-log/*` is served by the data router (src/routing/domains/changeLog.ts). */}
        <Route path={expensesPath} element={<ExpensesAdmin />} />
        <Route path={searchPath} element={<SearchPage />} />
        {/* `/warranty/*` is served by the data router (src/routing/domains/warranty.ts). */}
      </Route>

      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
}
